import "@blockdaemon/widget-host/global.css";
import "@blockdaemon/widget-host/polyfills";
import "./reduced-motion.css";
import {
  getColorSchemeOverride,
  queryClient,
  runWidgetBootstrap,
  WidgetApiError,
} from "@blockdaemon/widget-host";
import { WidgetAppProviders } from "@blockdaemon/widget-host/providers";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import packageJson from "../package.json";
import { createEarnRouter } from "./create-earn-router";
import { cacheWidgetConfig, fetchConfig } from "./features/customization/utils/config-loader";
import type { DefiTabRouteId } from "./features/defi/hooks/use-defi-tabs";
import { messages as enMessages } from "./locales/en-US/messages.mjs";
import { messages as jaMessages } from "./locales/ja-JP/messages.mjs";
import { messages as koMessages } from "./locales/ko-KR/messages.mjs";

async function preloadWidgetState() {
  try {
    cacheWidgetConfig(await fetchConfig());
  } catch (error) {
    if (error instanceof WidgetApiError && error.status === 401) {
      return;
    }

    console.error("Failed to preload widget state:", error);
  }
}

await runWidgetBootstrap({
  packageVersion: packageJson.version,
  appDisplayName: "Blockdaemon Earn Widget",
  i18nCatalogs: {
    "en-US": enMessages,
    "ko-KR": koMessages,
    "ja-JP": jaMessages,
  },
  preload: preloadWidgetState,
});

const router = createEarnRouter();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface HistoryState {
    backTo: DefiTabRouteId;
  }
}

async function startApp() {
  const rootElement = document.getElementById("root");

  if (!rootElement) {
    throw new Error("Root element not found");
  }

  const colorSchemeOverride = getColorSchemeOverride();

  const root = createRoot(rootElement);

  root.render(
    <StrictMode>
      <WidgetAppProviders queryClient={queryClient} forceColorScheme={colorSchemeOverride}>
        <RouterProvider router={router} />
      </WidgetAppProviders>
    </StrictMode>,
  );
}

void startApp();
